import React, { useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  IconButton,
  Button,
  Box,
  Slide,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ToastConfig } from '../../../../store/slices/uiSlice';
import { announce } from '../../../../utils/ariaLiveRegion';

interface ToastItemProps extends ToastConfig {
  onDismiss: (id: string) => void;
}

const variantConfig = {
  success: {
    severity: 'success' as const,
    icon: <CheckCircleIcon />,
    color: 'success.main',
  },
  error: {
    severity: 'error' as const,
    icon: <ErrorIcon />,
    color: 'error.main',
  },
  warning: {
    severity: 'warning' as const,
    icon: <WarningIcon />,
    color: 'warning.main',
  },
  info: {
    severity: 'info' as const,
    icon: <InfoIcon />,
    color: 'info.main',
  },
};

export const ToastItem: React.FC<ToastItemProps> = ({
  id,
  message,
  variant,
  duration,
  action,
  dismissible,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const config = variantConfig[variant];

  useEffect(() => {
    // Announce toast message to screen readers
    const politeness = variant === 'error' ? 'assertive' : 'polite';
    announce(message, politeness, 0); // Don't auto-clear, let the toast handle it

    // Auto-dismiss for success and info toasts
    if ((variant === 'success' || variant === 'info') && duration) {
      const timer = setTimeout(() => {
        onDismiss(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, variant, duration, message, onDismiss]);

  return (
    <Slide direction="left" in={true} mountOnEnter unmountOnExit>
      <Alert
        severity={config.severity}
        icon={config.icon}
        role="status"
        aria-live={variant === 'error' ? 'assertive' : 'polite'}
        aria-atomic="true"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {action && (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  action.onClick();
                  onDismiss(id);
                }}
                aria-label={action.label}
              >
                {action.label}
              </Button>
            )}
            {dismissible && (
              <IconButton
                size="small"
                aria-label={t('toast.dismiss')}
                color="inherit"
                onClick={() => onDismiss(id)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
        sx={{
          minWidth: 300,
          maxWidth: 500,
          boxShadow: 3,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        {message}
      </Alert>
    </Slide>
  );
};
